import type { ImportedTopologySource } from '../loaders/types'
import { rebaseImportedBodyOccurrenceBindingId } from './ImportedBodyBindingId'

/**
 * 将一个 bodyBindingId 记录表重建到指定 occurrence 身份。
 *
 * @param source 源记录表。
 * @param occurrenceNodeId 目标 occurrence 身份。
 * @param mapValue 需要同步更新记录值的转换器。
 * @returns 以 occurrence bodyBindingId 为 key 的新记录表。
 */
function rebaseBodyBindingRecord<T>(
  source: Readonly<Record<string, T>> | undefined,
  occurrenceNodeId: string,
  mapValue: (value: T, bodyBindingId: string) => T,
): Record<string, T> | undefined {
  if (!source) {
    return undefined
  }

  const result: Record<string, T> = {}
  Object.entries(source).forEach(([sourceBodyBindingId, value]) => {
    const occurrenceBodyBindingId = rebaseImportedBodyOccurrenceBindingId(
      sourceBodyBindingId,
      occurrenceNodeId,
    )
    if (!occurrenceBodyBindingId) {
      throw new Error(`拓扑源存在空 bodyBindingId：occurrence=${occurrenceNodeId}`)
    }
    if (Object.prototype.hasOwnProperty.call(result, occurrenceBodyBindingId)) {
      throw new Error(
        `拓扑源 bodyBindingId 重绑定冲突：occurrence=${occurrenceNodeId}, bodyBindingId=${occurrenceBodyBindingId}`,
      )
    }
    result[occurrenceBodyBindingId] = mapValue(value, occurrenceBodyBindingId)
  })
  return result
}

/**
 * 将单个导入拓扑源的 body 身份重绑定到指定 occurrence。
 *
 * 该操作只重建 occurrence 专属的 body 条目、绑定表和反查索引。Geometry、BRep 引用、
 * MeshData 以及 primitive/mesh definition 目录继续共享，避免相同零件实例重复占用内存。
 *
 * @param source definition 或已有 occurrence 作用域的拓扑源。
 * @param occurrenceNodeId 新的 occurrence 节点身份。
 * @returns occurrence 身份唯一、definition 资产仍共享的新拓扑源。
 */
export function rebaseImportedTopologySourceOccurrence(
  source: ImportedTopologySource,
  occurrenceNodeId: string,
): ImportedTopologySource {
  const normalizedOccurrenceNodeId = occurrenceNodeId.trim()
  if (!normalizedOccurrenceNodeId) {
    throw new Error('拓扑源重绑定缺少 occurrenceNodeId')
  }

  const bodyBrepBindings = rebaseBodyBindingRecord(
    source.bodyBrepBindings,
    normalizedOccurrenceNodeId,
    (binding, bodyBindingId) => ({ ...binding, bodyBindingId }),
  )
  const visibleBoundsByBodyBindingId = rebaseBodyBindingRecord(
    source.visibleBoundsByBodyBindingId,
    normalizedOccurrenceNodeId,
    (bounds) => bounds,
  )
  const bodyTopologyAssets = rebaseBodyBindingRecord(
    source.bodyTopologyAssets,
    normalizedOccurrenceNodeId,
    (asset, bodyBindingId) => ({ ...asset, bodyBindingId }),
  )
  const bodyBrepBindingIdByPrimitiveId = source.bodyBrepBindingIdByPrimitiveId
    ? Object.fromEntries(
        Object.entries(source.bodyBrepBindingIdByPrimitiveId).map(([primitiveId, bodyBindingId]) => [
          primitiveId,
          rebaseImportedBodyOccurrenceBindingId(bodyBindingId, normalizedOccurrenceNodeId),
        ]),
      )
    : undefined

  return {
    ...source,
    bodyEntries: source.bodyEntries.map((entry) => ({
      ...entry,
      bodyBindingId: entry.bodyBindingId
        ? rebaseImportedBodyOccurrenceBindingId(entry.bodyBindingId, normalizedOccurrenceNodeId)
        : entry.bodyBindingId,
    })),
    bodyBrepBindings,
    bodyBrepBindingIdByPrimitiveId,
    visibleBoundsByBodyBindingId,
    bodyTopologyAssets,
  }
}
